// Temper colours encode work: t = clamp((log2(difficulty) - 20) / (44 - 20), 0, 1)
// sampled from a continuous ramp straw -> bronze -> purple -> blue -> grey-blue (DESIGN_SYSTEM.md).
export const TEMPER_RAMP = ['#EFE3B8', '#D9B85C', '#B7773A', '#8A4B78', '#4C63B6', '#6F8FA6'] as const;
export const WHITE_HEAT = '#FFF6E0';

const LOG2_MIN = 20;
const LOG2_MAX = 44;

type RGB = [number, number, number];

function hexToRgb(hex: string): RGB {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHex([r, g, b]: RGB): string {
  return `#${[r, g, b].map((v) => Math.round(v).toString(16).padStart(2, '0')).join('').toUpperCase()}`;
}

const RAMP_RGB = TEMPER_RAMP.map(hexToRgb);

/** Position on the temper ramp for a difficulty in expected hashes (0 = pale straw, 1 = grey-blue). */
export function temperT(difficulty: number): number {
  if (!(difficulty > 0) || !Number.isFinite(difficulty)) return difficulty === Infinity ? 1 : 0;
  const t = (Math.log2(difficulty) - LOG2_MIN) / (LOG2_MAX - LOG2_MIN);
  return Math.min(1, Math.max(0, t));
}

/** Sample the ramp at t in [0, 1] (linear RGB interpolation between the six stops). */
export function temperAt(t: number): string {
  const x = Math.min(1, Math.max(0, t)) * (RAMP_RGB.length - 1);
  const i = Math.min(RAMP_RGB.length - 2, Math.floor(x));
  const f = x - i;
  const a = RAMP_RGB[i]!;
  const b = RAMP_RGB[i + 1]!;
  return rgbToHex([a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f, a[2] + (b[2] - a[2]) * f]);
}

export const temperColor = (difficulty: number) => temperAt(temperT(difficulty));

/** Mix two hex colours (used for the quench: white heat cooling to the temper colour). */
export function mixHex(from: string, to: string, f: number): string {
  const a = hexToRgb(from);
  const b = hexToRgb(to);
  const k = Math.min(1, Math.max(0, f));
  return rgbToHex([a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k, a[2] + (b[2] - a[2]) * k]);
}

/** The temper colour lifted toward parchment, for text on the night background (keeps contrast at the purple/blue end). */
export const temperInk = (difficulty: number) => mixHex(temperColor(difficulty), '#F2EAD8', 0.35);
